'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourierIntegration } from '../../models';
import { useManifestsFilters } from '../../hooks/useManifestsFilters';

interface ManifestsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  courierIntegrations?: CourierIntegration[];
  className?: string;
}

export const ManifestsFiltersPanel = ({
  initialConditions = [],
  onChange,
  courierIntegrations = [],
  className,
}: ManifestsFiltersPanelProps) => {
  const { t } = useTranslation('manifestsModule');

  const { filters, handleStatusChange, handleCourierChange, handleCreatedAtChange, clearAllFilters } =
    useManifestsFilters({ initialConditions, onChange });

  const statusOptions = useMemo(
    () => [
      { value: 'GENERADO', label: t('status.GENERADO') },
      { value: 'COMPLETADO', label: t('status.COMPLETADO') },
      { value: 'ANULADO', label: t('status.ANULADO') },
    ],
    [t],
  );

  const courierOptions = useMemo(
    () =>
      courierIntegrations.map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [courierIntegrations],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.courier_integration_id) count++;
    if (filters.created_at?.from && filters.created_at?.to) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.section_basic', 'Información básica')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.status')}>
            <Combobox
              options={statusOptions}
              value={filters.status}
              setValue={handleStatusChange}
              placeholder={t('filters.select_status', 'Seleccionar estado')}
              dataTestId="filter-status"
              buttonClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.courier')}>
            <Combobox
              options={courierOptions}
              value={filters.courier_integration_id}
              setValue={handleCourierChange}
              placeholder={t('filters.select_courier', 'Seleccionar courier')}
              dataTestId="filter-courier"
              buttonClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.created_at')}>
            <DateRangePicker
              initialDateFrom={filters.created_at?.from}
              initialDateTo={filters.created_at?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            {t('filters.clear', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">{children}</div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
